/* Registro de trabajo SIN ENVIAR: una sola respuesta a "¿perder esta pestaña
 * perdería algo?" para todo lo que puede cerrarla o abandonarla.
 *
 * POR QUÉ ES UN REGISTRO Y NO UNA PROP. Quien abandona la pestaña no es el
 * editor: es la barra del navegador (cerrar/recargar), un `<Link>`, el selector
 * de proyecto o el banner de actualización. Cada uno necesita la misma
 * pregunta y la misma respuesta, y el editor no debe conocerlos a todos. El
 * editor se REGISTRA aquí; los demás consultan.
 *
 * Distingue dos clases de pérdida, porque no avisan lo mismo:
 *   · RECUPERABLE — hay un borrador guardado; al volver reaparece.
 *   · NO RECUPERABLE — ficheros locales aún sin subir (no se pueden serializar)
 *     o un borrador que no se pudo guardar (cuota / modo privado). Ahí el aviso
 *     tiene que decir que se pierde de verdad.
 *
 * Módulo puro y sin dependencias (el gate corre los `.test.mjs` con node).
 */

const sources = new Map();
let allowUntil = 0;

/**
 * Registra una fuente. `read()` devuelve
 *   { dirty, localFiles?: string[], storageFailed?: boolean, label?: string }.
 * Devuelve la función que la retira (idempotente, y no retira una fuente
 * registrada después con el mismo id).
 */
export function registerUnsavedWork(id, read) {
  sources.set(id, read);
  return () => { if (sources.get(id) === read) sources.delete(id); };
}

export function unsavedWorkState() {
  const labels = [];
  const localFiles = [];
  let dirty = false;
  let storageFailed = false;
  for (const read of sources.values()) {
    let r = null;
    try { r = read(); } catch (_) { r = null; }
    if (!r || !r.dirty) continue;
    dirty = true;
    if (r.label) labels.push(r.label);
    if (r.storageFailed) storageFailed = true;
    for (const f of r.localFiles || []) localFiles.push(f);
  }
  return {
    dirty, labels, localFiles, storageFailed,
    recoverable: dirty && !storageFailed && localFiles.length === 0,
  };
}

export function hasUnsavedWork() {
  return unsavedWorkState().dirty;
}

/** Texto del aviso propio de la app (diálogos, no `beforeunload`, que el
 *  navegador no deja personalizar). */
export function leaveMessage(state, action = "salir") {
  const head = action === "actualizar"
    ? "Vas a recargar Orca Deck para actualizar."
    : action === "proyecto" ? "Vas a cambiar de proyecto."
      : "Vas a salir de esta pantalla.";
  if (!state.dirty) return head;
  const parts = [head];
  if (state.localFiles.length) {
    parts.push(`Hay archivos locales sin subir que NO se pueden recuperar: ${state.localFiles.join(", ")}. `
      + "Tendrás que volver a elegirlos.");
  }
  if (state.storageFailed) {
    parts.push("No se pudo guardar el borrador en este navegador (almacenamiento lleno o bloqueado): "
      + "lo que no hayas enviado se perderá.");
  } else {
    parts.push("El prompt y las referencias subidas quedan en un borrador que se recupera al volver a esta pestaña.");
  }
  parts.push("¿Continuar?");
  return parts.join("\n\n");
}

/**
 * ¿Se puede abandonar? Sin trabajo pendiente, sí sin preguntar; con él,
 * pregunta con `confirmFn` (por defecto `window.confirm`).
 */
export function confirmLeave(action = "salir", { confirmFn } = {}) {
  const state = unsavedWorkState();
  if (!state.dirty) return true;
  const ask = confirmFn || (typeof window !== "undefined" ? window.confirm.bind(window) : () => true);
  return !!ask(leaveMessage(state, action));
}

/** El próximo `beforeunload` (en los 2 s siguientes) no pregunta: la recarga ya
 *  la confirmó el usuario en un diálogo propio y preguntar otra vez es ruido. El
 *  plazo evita que una recarga cancelada deje el aviso desarmado. */
export function allowNextUnload(now = Date.now) { allowUntil = now() + 2000; }

/**
 * Instala el aviso nativo al cerrar/recargar. `extraGuard` suma otras causas
 * (subidas en vuelo). Devuelve el desinstalador.
 */
export function installUnloadGuard(win, extraGuard = () => false) {
  const onBeforeUnload = (e) => {
    if (allowUntil && Date.now() <= allowUntil) { allowUntil = 0; return undefined; }
    if (!hasUnsavedWork() && !extraGuard()) return undefined;
    e.preventDefault();
    e.returnValue = "";
    return "";
  };
  win.addEventListener("beforeunload", onBeforeUnload);
  return () => win.removeEventListener("beforeunload", onBeforeUnload);
}

/** Solo para tests. */
export function _resetUnsavedWork() { sources.clear(); allowUntil = 0; }
